import { forwardRef } from 'react';

export type EmptyStateVariant = 'default' | 'search' | 'filter' | 'error' | 'inbox';

export interface EmptyStateProps {
  variant?: EmptyStateVariant;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

const defaultContent: Record<
  EmptyStateVariant,
  { title: string; description: string; icon: React.ReactNode }
> = {
  default: {
    title: '데이터가 없습니다',
    description: '아직 등록된 항목이 없습니다.',
    icon: <EmptyBoxIcon />,
  },
  search: {
    title: '검색 결과가 없습니다',
    description: '다른 검색어로 다시 시도해 보세요.',
    icon: <SearchIcon />,
  },
  filter: {
    title: '필터 결과가 없습니다',
    description: '선택한 필터에 해당하는 항목이 없습니다.',
    icon: <FilterIcon />,
  },
  error: {
    title: '오류가 발생했습니다',
    description: '잠시 후 다시 시도해 주세요.',
    icon: <ErrorIcon />,
  },
  inbox: {
    title: '받은 메시지가 없습니다',
    description: '새로운 문의가 도착하면 여기에 표시됩니다.',
    icon: <InboxIcon />,
  },
};

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    { variant = 'default', title, description, icon, action, className = '' },
    ref
  ) => {
    const content = defaultContent[variant];

    return (
      <div
        ref={ref}
        className={`flex flex-col items-center justify-center py-16 px-6 text-center ${className}`}
      >
        <div className="w-20 h-20 mb-6 text-text/30">
          {icon || content.icon}
        </div>
        <h3 className="font-en text-xl font-semibold text-text mb-2">
          {title || content.title}
        </h3>
        <p className="font-kr text-text/60 max-w-md mb-6">
          {description || content.description}
        </p>
        {action && <div className="mt-2">{action}</div>}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';

// Icon components
function EmptyBoxIcon() {
  return (
    <svg
      className="w-full h-full"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      className="w-full h-full"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
      />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg
      className="w-full h-full"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
      />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      className="w-full h-full"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
      />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg
      className="w-full h-full"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859m-17.5 0a2.25 2.25 0 0 0-2.25 2.25v1.5a2.25 2.25 0 0 0 2.25 2.25h17.5a2.25 2.25 0 0 0 2.25-2.25v-1.5a2.25 2.25 0 0 0-2.25-2.25m-17.5 0V6.108c0-1.135.845-2.098 1.976-2.192.373-.031.747-.057 1.123-.08m15.272 9.464V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-9.774 0-.015.002a48.194 48.194 0 0 0-3.478.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
      />
    </svg>
  );
}

// Specialized empty state components
export interface NoResultsProps {
  searchQuery?: string;
  onClear?: () => void;
  className?: string;
}

export function NoResults({ searchQuery, onClear, className = '' }: NoResultsProps) {
  return (
    <EmptyState
      variant="search"
      title={searchQuery ? `"${searchQuery}"에 대한 검색 결과가 없습니다` : '검색 결과가 없습니다'}
      description="다른 검색어로 다시 시도하거나 필터를 조정해 보세요."
      action={
        onClear && (
          <button
            onClick={onClear}
            className="px-4 py-2 font-kr text-sm text-accent hover:text-accent/80 transition-colors"
          >
            검색어 지우기
          </button>
        )
      }
      className={className}
    />
  );
}

export interface NoPostsProps {
  contentType?: 'work' | 'lab';
  onCreate?: () => void;
  className?: string;
}

export function NoPosts({ contentType = 'work', onCreate, className = '' }: NoPostsProps) {
  const typeLabel = contentType === 'work' ? 'Work' : 'Lab';

  return (
    <EmptyState
      variant="default"
      title={`${typeLabel} 항목이 없습니다`}
      description={`첫 번째 ${typeLabel} 항목을 만들어 보세요.`}
      action={
        onCreate && (
          <button
            onClick={onCreate}
            className="px-6 py-2.5 bg-accent text-white font-en text-sm rounded-lg hover:bg-accent/90 transition-colors"
          >
            새 {typeLabel} 만들기
          </button>
        )
      }
      className={className}
    />
  );
}

export interface NoContactsProps {
  className?: string;
}

export function NoContacts({ className = '' }: NoContactsProps) {
  return (
    <EmptyState
      variant="inbox"
      title="문의가 없습니다"
      description="새로운 문의가 도착하면 여기에 표시됩니다."
      className={className}
    />
  );
}

export interface ErrorStateProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({
  title = '오류가 발생했습니다',
  description = '잠시 후 다시 시도해 주세요.',
  onRetry,
  className = '',
}: ErrorStateProps) {
  return (
    <EmptyState
      variant="error"
      title={title}
      description={description}
      action={
        onRetry && (
          <button
            onClick={onRetry}
            className="px-6 py-2.5 bg-text text-white font-kr text-sm rounded-lg hover:bg-text/90 transition-colors"
          >
            다시 시도
          </button>
        )
      }
      className={className}
    />
  );
}